import gql from 'graphql-tag';
import {
  omit,
  keys,
  clone,
  cloneDeep,
  isArray,
  isUndefined,
  mergeWith,
  mapValues,
  debounce,
  findIndex,
  uniqBy,
} from 'lodash';

import apolloClient from '../providers/casesService';
import instanceEvents from './utils/instanceEvents';

const ROWS_PER_PAGE = 25;

export default function dataView(
  options = { append: false, dontFetchOnLoad: false },
) {
  return Component => ({
    name: `${Component.name}-container`,
    created() {
      if (options.queries.fetch && !options.dontFetchOnLoad) {
        this.changePage(1);
      }
      if (options.queries.fetchById && this.id) {
        this.fetchById(this.id);
      }
    },
    data() {
      return {
        state: {
          firstLoad:
            (!!options.queries.fetch && !options.dontFetchOnLoad) ||
            (options.queries.fetchById && this.id),
          loading: false,
          submitting: false,
          loadingItemId: null,
          savingItemId: null,
          errors: {
            fetch: [],
            fetchById: [],
            submit: [],
            save: [],
          },
          page: 1,
          data: [],
          preSearchData: null,
          queryData: {},
          itemData: null,
          oldItemData: null,
          lastPageReached: false,
          sortBy: options.sortBy || null,
          searchTerm: null,
          queryContext: null,
          filter: null,
        },
      };
    },
    methods: {
      async fetch(offset, limit, additionalArguments = {}) {
        if (this.subscription) {
          this.subscription.unsubscribe();
        }
        this.state.errors.fetch = [];
        this.state.loading = true;
        try {
          const query = apolloClient.watchQuery({
            query: gql(options.queries.fetch),
            variables: {
              offset,
              limit,
              sortBy: this.state.sortBy,
              search: this.state.searchTerm,
              queryContext: this.state.queryContext,
              filter: this.state.filter,
              ...additionalArguments,
            },
            fetchPolicy: 'no-cache',
          });
          this.subscription = query.subscribe({
            next: ({ data: responseData }) => {
              const dataKey = keys(responseData)[0];
              const data = responseData[dataKey];

              // Reset the data if we are not in append mode or this is
              // the first results of a search, otherwise append
              if (!options.append || (this.state.searchTerm && offset === 0)) {
                this.state.data = clone(data);
              } else {
                this.state.data = uniqBy(this.state.data.concat(data), 'id');
              }
              if (data.length < limit) {
                this.state.lastPageReached = true;
              }
              this.state.loading = false;
              this.state.firstLoad = false;
            },
            error: (error) => {
              console.error(error);
              this.state.firstLoad = false;
              this.state.errors.fetch = error.networkError
                ? [error.networkError]
                : error.graphQLErrors;
            },
          });
        } catch (e) {
          console.error(e);
          this.state.errors.fetch = e.networkError ? [e.networkError] : e.graphQLErrors;
        }
      },
      async fetchOne(id) {
        this.state.errors.fetch = [];
        this.state.loadingItemId = id;
        try {
          const response = await apolloClient.query({
            query: gql(options.queries.fetchById),
            variables: {
              id,
              queryContext: this.state.queryContext,
            },
            fetchPolicy: 'no-cache',
          });
          const data = response.data.caseConfigurations[0];
          if (data) {
            this.state.data.unshift(data);
            this.state.data = uniqBy(this.state.data, 'id');
          }
        } catch (e) {
          console.error(e);
          this.state.errors.fetch = e.networkError ? [e.networkError] : e.graphQLErrors;
        } finally {
          this.state.loadingItemId = null;
          this.state.firstLoad = false;
        }
      },
      async changeSorting(sortBy) {
        this.state.sortBy = sortBy;
        this.state.data = [];
        // wait for next render cycle to ensure state.data watchers are triggered
        this.$nextTick(() => {
          this.changePage(1);
        });
      },
      async search(term) {
        this.state.searchTerm = term;
        // store the presearch data so that
        // we can reset to it when the search is cleared,
        // even before the query completes
        // this way, we can instantly display the previous
        // list to the user (feels more snappy)
        if (term && !this.state.preSearchData) {
          this.state.preSearchData = clone(this.state.data);
        }
        // reset to the previous data before triggering the search
        if (!term && this.state.preSearchData) {
          this.state.data = this.state.preSearchData;
          this.state.preSearchData = null;
        }
        this.triggerSearch();
      },
      filter(filter) {
        this.state.filter = filter;
        this.state.data = [];
        // To prevent the data from resetting to the wrong
        // state after this sequence of actions
        // search -> filter -> clear search
        this.state.preSearchData = null;
        this.$nextTick(() => {
          this.changePage(1);
        });
      },
      triggerSearch: debounce(function triggerSearch() {
        this.$nextTick(() => {
          this.changePage(1);
        });
      }, 500),
      async fetchById(id) {
        this.state.errors.fetch = [];
        this.state.loadingItemId = id;
        try {
          const response = await apolloClient.query({
            query: gql(options.queries.fetchById),
            variables: {
              id,
              queryContext: this.state.queryContext,
            },
            fetchPolicy: 'no-cache',
          });
          const { data } = response;
          const dataKey = keys(data)[0];
          this.state.itemData = cloneDeep(data[dataKey][0]);
          this.state.oldItemData = cloneDeep(this.state.itemData);
        } catch (e) {
          console.error(e);
          this.state.errors.fetch = e.networkError ? [e.networkError] : e.graphQLErrors;
        } finally {
          this.state.loadingItemId = null;
          this.state.firstLoad = false;
        }
      },
      async add(value, successCallback, errorCallback) {
        this.state.submitting = true;
        this.state.errors.submit = [];
        try {
          const { data: responseData } = await apolloClient.mutate({
            mutation: gql(options.queries.add),
            variables: {
              value,
              queryContext: this.state.queryContext,
            },
            refetchQueries: options.queries.fetch ? [this.getFetchQuery()] : [],
          });
          const dataKey = keys(responseData)[0];
          const data = responseData[dataKey];
          this.$emit('added', data.id);
          if (successCallback) {
            successCallback(data);
          }
        } catch (e) {
          this.state.errors.submit = e.networkError ? [e.networkError] : e.graphQLErrors;
          if (errorCallback) {
            errorCallback(e);
          }
        } finally {
          this.state.submitting = false;
        }
      },
      async save(item, successCallback, errorCallback, args) {
        this.state.savingItemId = item.id;
        this.state.errors.save = [];

        const index = findIndex(this.state.data, { id: item.id });
        if (this.state.data.length) {
          this.state.itemData = this.state.data[index];
        }
        this.state.oldItemData = cloneDeep(this.state.itemData);
        this.state.itemData = mergeWith({}, this.state.itemData, item, (objValue, srcValue, key) => {
          // Case logic nodes should not be merged with
          // existing local data, but everything else should be
          if (key === 'nodes') {
            return srcValue;
          }
          return undefined;
        });
        if (this.state.data.length) {
          this.state.data.splice(index, 1, this.state.itemData);
        }
        try {
          await apolloClient.mutate({
            mutation: gql(options.queries.save),
            variables: {
              value: item,
              queryContext: this.state.queryContext,
              ...args,
            },
          });
          if (successCallback) {
            successCallback();
          }
        } catch (e) {
          console.error(e);
          this.state.errors.save = e.networkError ? [e.networkError] : e.graphQLErrors;
          this.state.itemData = this.state.oldItemData;
          if (this.state.data.length) {
            this.state.data.splice(index, 1, this.state.oldItemData);
          }
          if (errorCallback) {
            errorCallback(e);
          }
        } finally {
          this.state.savingItemId = null;
        }
      },
      async remove(items, callback) {
        // eslint-disable-next-line no-param-reassign
        items = isArray(items) ? items : [items];
        this.state.loading = true;
        try {
          const ids = items.map(item => item.id);
          const { offset, limit } = this.offsetLimit();
          await apolloClient.mutate({
            mutation: gql(options.queries.remove),
            variables: {
              ids,
              queryContext: this.state.queryContext,
            },
            refetchQueries: options.queries.fetch && (
                options.append
                  ? // eslint-disable-next-line no-mixed-operators
                    [this.getFetchQuery(offset + limit - 1, 1)]
                  : [this.getFetchQuery()]
              ),
          });
          this.state.data = this.state.data.filter(
            item => !ids.includes(item.id),
          );
          if (callback) {
            callback();
          }
        } finally {
          this.state.loading = false;
        }
      },
      async queryOrMutate(queryKey, variables, successCallback, errorCallback) {
        const query = gql(options.queries[queryKey]);
        try {
          if (query.definitions[0].operation === 'mutation') {
            const response = await apolloClient.mutate({
              mutation: query,
              variables,
              refetchQueries: options.queries.fetch
                ? [this.getFetchQuery()]
                : [],
            });
            if (successCallback) {
              successCallback(response.data)
            }
          } else {
            const response = await apolloClient.query({
              query,
              variables,
            });
            const { data } = response;
            this.state.queryData[queryKey] = data;
            if (successCallback) {
              successCallback(data)
            }
          }
        } catch (e) {
          this.state.errors[queryKey] = e.graphQLErrors;
          if (errorCallback) {
            errorCallback(e);
          }
        }
      },
      changePage(page, additionalArguments) {
        if (page <= 0) {
          return;
        }
        if (this.state.lastPageReached && page > this.state.page) {
          return;
        }
        this.state.page = page;
        this.state.lastPageReached = false;
        const { offset, limit } = this.offsetLimit();
        this.fetch(offset, limit, additionalArguments);
      },
      offsetLimit() {
        return {
          offset: (this.state.page - 1) * ROWS_PER_PAGE,
          limit: ROWS_PER_PAGE,
        };
      },
      getFetchQuery(offset, limit) {
        return {
          query: gql(options.queries.fetch),
          variables: {
            ...(isUndefined(offset) ? this.offsetLimit() : { offset, limit }),
            sortBy: this.state.sortBy,
            search: this.state.searchTerm,
          },
        };
      },
      setQueryContext(queryContext) {
        this.state.queryContext = queryContext;
      },
    },
    props: {
      id: {
        type: String,
      },
      ...omit(Component.props, ['loading', 'page', 'data', 'itemData']),
    },
    render(createElement) {
      if (this.state.firstLoad) {
        return (
          <div
            style={{ height: '100px' }}
            v-loading={{ value: true, body: true }}
          />
        );
      }
      return createElement(
        Component,
        {
          props: {
            ...this.$props,
            data: this.state.data,
            itemData: this.state.itemData,
            page: this.state.page,
            loading: this.state.loading,
            loadingItemId: this.state.loadingItemId,
            submitting: this.state.submitting,
            savingItemId: this.state.savingItemId,
            saving: this.state.submitting || !!this.state.savingItemId,
            errors: this.state.errors,
            fetchErrors: this.state.errors.fetch,
            submitErrors: this.state.errors.submit,
            saveErrors: this.state.errors.save,
            endReached: this.state.lastPageReached,
          },
          on: {
            ...instanceEvents(this),
            // call the query/mutate method if the child emits an event
            // that corresponds to a query/mutation key (e.g. emit the 'duplicate' event
            // to run the duplicate mutation)
            ...mapValues(options.queries, (query, key) =>
              this.queryOrMutate.bind(this, key),
            ),
            changePage: this.changePage,
            loadMore: additionalArguments =>
              this.changePage(this.state.page + 1, additionalArguments),
            fetch: additionalArguments =>
              this.fetch(null, null, additionalArguments),
            fetchOne: id => this.fetchOne(id),
            setQueryContext: this.setQueryContext,
            remove: this.remove,
            add: this.add,
            save: this.save,
            fetchById: this.fetchById,
            sort: this.changeSorting,
            search: this.search,
            filter: this.filter,
          },
        },
        this.$slots.default,
      );
    },
  });
}
